import { type ApolloCache, ApolloLink } from "@apollo/client";

import type { GetIssueQuery } from "@medbillai/generated-graphql-types";

import { getLoadingAiResponseEventId } from "./loadingEvents";
import { getIssueQuery, getIssueQueryVariables } from "./queries";
import {
  isFullReadOrderingEnabled,
  isStaleRead,
  markReadApplied,
  startRead,
} from "./readOrder";
import { forgetRowsReadById, mergeNarrowIssue } from "./turnTail";

type GetIssueEvent = NonNullable<
  NonNullable<GetIssueQuery["issue__Issue"]>[number]["userVisibleEvents"]
>[number];

/**
 * The upload card the client writes before the server has a row for it. A
 * full read replaces the transcript, which drops it; a stale one must too,
 * or the card stays beside the real row read by id.
 */
const isClientUploadCard = (
  event: GetIssueEvent,
  readIds: ReadonlySet<string>,
): boolean =>
  event.__typename === "issue__DocumentUploadedEvent_Type" &&
  (event.uploading === true || event.uploadFailed === true) &&
  !readIds.has(event.id);

/** The row ids in an untyped link result. */
const rowIds = (events: unknown): ReadonlySet<string> =>
  new Set(
    Array.isArray(events)
      ? events.flatMap((event: unknown) =>
          typeof event === "object" &&
          event !== null &&
          "id" in event &&
          typeof event.id === "string"
            ? [event.id]
            : [],
        )
      : [],
  );

/**
 * Puts every full GetIssue read into the transcript's read order, whoever
 * starts it: the case query, a guarded or unguarded refetch, or a mutation's
 * `refetchQueries`. A result that finishes behind a newer read is folded in
 * like a stale narrow read, adding only the rows the cache lacks, so it cannot
 * drop newer rows or roll the issue back.
 */
export const createGetIssueReadOrderLink = (cache: ApolloCache<unknown>) =>
  new ApolloLink((operation, forward) => {
    const { id, userId } = operation.variables;
    if (
      operation.operationName !== "GetIssue" ||
      typeof id !== "string" ||
      typeof userId !== "string" ||
      !isFullReadOrderingEnabled(id)
    ) {
      return forward(operation);
    }
    const readNumber = startRead(id);
    return forward(operation).map(result => {
      const narrow = result.data?.issue__Issue?.[0];
      if (!narrow || result.errors?.length) {
        return result;
      }
      if (!isStaleRead(id, readNumber)) {
        markReadApplied(id, readNumber);
        forgetRowsReadById(id);
        return result;
      }
      const cached = cache.readQuery({
        query: getIssueQuery,
        variables: getIssueQueryVariables(id, userId),
      });
      const cachedIssue = cached?.issue__Issue?.[0];
      if (!cached || !cachedIssue) {
        return result;
      }
      const readIds = rowIds(narrow.userVisibleEvents);
      const outcome = mergeNarrowIssue({
        cached: {
          ...cached,
          issue__Issue: [
            {
              ...cachedIssue,
              userVisibleEvents: (cachedIssue.userVisibleEvents ?? []).filter(
                event => !isClientUploadCard(event, readIds),
              ),
            },
            ...(cached.issue__Issue ?? []).slice(1),
          ],
        },
        narrow: { id: narrow.id, userVisibleEvents: narrow.userVisibleEvents },
        loadingEventId: getLoadingAiResponseEventId(id),
        dropsLoadingEvent: false,
        refreshesKnownRows: false,
      });
      return outcome.complete ? { ...result, data: outcome.data } : result;
    });
  });
